/* @component id="auth-button" name="ProfileButton" type="ui-auth" version="1.8.0" */
"use client";

import { signIn, signOut } from "next-auth/react";
import Link from "next/link";
import { Button } from "../ui/button";
import useAuthorization from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { UserCircle2 } from "lucide-react";

export default function ProfileButton() {
  const { session, location, locationGroups, setLocation } = useAuthorization();

  if (!session) {
    return (
      <Button
        className="w-30 m-auto focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        variant="link"
        onClick={() => signIn("azure-ad")}
      >
        Sign in
      </Button>
    );
  }

  const handleLocationChange = (azureGroupId: string) => {
    const next = locationGroups.find((loc) => loc.azure_group_id === azureGroupId);

    // This updates the auth hook state AND writes to localStorage
    setLocation(next ?? null);

    // Force all pages/components to remount and pick up the new cached location
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  const displayName = session.user?.name ?? session.user?.email ?? "Signed in user";
  const email = session.user?.email ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 px-3 py-1 text-sm focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
        >
          <UserCircle2 className="h-5 w-5 shrink-0" />
          {location && (
            <span className="text-[11px] uppercase rounded-full border px-2 py-0.5 max-w-[200px] truncate">
              {location.name}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        {/* Profile summary */}
        <DropdownMenuItem className="cursor-default focus:bg-transparent focus:outline-none focus:ring-0">
          <div className="flex flex-col items-start gap-0.5 w-full">
            <span className="text-xs text-black font-medium truncate w-full">{displayName}</span>
            {email && <span className="text-[11px]  truncate w-full">{email}</span>}
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* My Requests */}
        <DropdownMenuItem asChild>
          <Link
            href="https://support.gazl.co/hc/en-us/requests"
            target="_blank"
            className="hover:cursor-pointer hover:bg-slat w-full focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          >
            My Requests
          </Link>
        </DropdownMenuItem>

        {locationGroups.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1 text-[11px] font-medium">Current location</div>
            <DropdownMenuRadioGroup
              value={location?.azure_group_id ?? ""}
              onValueChange={handleLocationChange}
            >
              {locationGroups.map((loc) => (
                <DropdownMenuRadioItem
                  key={loc.azure_group_id}
                  value={loc.azure_group_id}
                  className="text-xs focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <span className="mr-2 inline-flex h-4 w-4 items-center justify-center rounded-full border text-[9px] uppercase">
                    {loc.country}
                  </span>
                  <span className="truncate">{loc.name}</span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
          onClick={() =>
            signOut({
              callbackUrl: `https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
                window.location.origin,
              )}`,
            })
          }
        >
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
